import { inject, Injectable } from '@angular/core';
import { userStore } from '../stores/user.store';
import {
  HttpRequest,
  HttpHandlerFn,
  HttpInterceptorFn,
} from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';
import { configStore } from '../stores/config.store';
import { LoadingService } from './loading.service'; // Adjust path

import { finalize } from 'rxjs';

const PUBLIC_URLS = [
  `${environment.base_eruauth_url}/eru/eru/login`,
  `${environment.base_eruauth_url}/eru/eru/userinfo`,
];

// Origins of the central eru project; only these may receive the central user token
const CENTRAL_ORIGINS = [
  environment.base_eruauth_url,
  environment.base_erufunc_url,
  environment.base_eruql_url,
  environment.base_eruai_url,
].map((url) => originOf(url)).filter((origin): origin is string => !!origin);

const SERVICE_TAB_PREFIXES = ['/eruauth', '/erufunctions', '/eruql', '/erufiles', '/eruai', '/erugateway'];

function originOf(url: string): string | null {
  try {
    return new URL(url, window.location.origin).origin;
  } catch {
    return null;
  }
}

function stripTrailingSlash(url: string): string {
  return url.replace(/\/+$/, '');
}

function urlHasPrefix(url: string, prefix: string): boolean {
  const p = stripTrailingSlash(prefix);
  return !!p && (url === p || url.startsWith(p + '/') || url.startsWith(p + '?'));
}

/**
 * Per-instance credentials for requests made outside a service tab (compare / import),
 * keyed by URL prefix. Kept separate from the tab's own requestHeader.
 */
@Injectable({ providedIn: 'root' })
export class InstanceAuthRegistry {
  private entries = new Map<string, Record<string, string>>();

  register(urlPrefix: string, headers: Record<string, string>) {
    this.entries.set(stripTrailingSlash(urlPrefix), headers);
  }

  unregister(urlPrefix: string) {
    this.entries.delete(stripTrailingSlash(urlPrefix));
  }

  match(url: string): Record<string, string> | null {
    let best: string | null = null;
    for (const prefix of this.entries.keys()) {
      if (urlHasPrefix(url, prefix) && (!best || prefix.length > best.length)) {
        best = prefix;
      }
    }
    return best ? this.entries.get(best) ?? null : null;
  }
}

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {
  const router = inject(Router);
  const loadingService = inject(LoadingService);
  const registry = inject(InstanceAuthRegistry);
  // router.url is '/' until the first navigation of a freshly opened tab completes
  const currentUrl = router.url && router.url !== '/' ? router.url : window.location.pathname;
  let headers: Record<string, string> = {};

  loadingService.startLoading();

  try {
    const instanceHeaders = registry.match(req.url);
    const isCentral = CENTRAL_ORIGINS.includes(originOf(req.url) || '');
    const isServiceTab = SERVICE_TAB_PREFIXES.some((prefix) => urlHasPrefix(currentUrl, prefix));

    if (instanceHeaders) {
      headers = instanceHeaders;
    } else if (isCentral) {
      const store = inject(userStore);
      const idToken = store.user()?.login_response?.id_token || '';
      const accessToken = store.user()?.login_response?.access_token || '';
      if (idToken) headers['id_token'] = idToken;
      if (accessToken) headers['Authorization'] = accessToken;
    } else if (isServiceTab && originOf(req.url) !== window.location.origin) {
      // the UI's own assets never need the instance token
      const header = inject(configStore).requestHeader();
      if (header?.idToken) headers[header.idTokenKey || 'id_token'] = header.idToken;
      if (header?.value) headers[header.key || 'Authorization'] = header.value;
    }

    const request = Object.keys(headers).length ? req.clone({ setHeaders: headers }) : req;
    return next(request).pipe(
      finalize(() => loadingService.stopLoading())
    );
  } catch (error) {
    console.error('Error in authInterceptor:', error);
    loadingService.stopLoading();
    return next(req);
  }
};
